import React, { useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import websitelogo from '../../../../assets/logos/websitelogo.svg';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import CallIcon from '@mui/icons-material/Call';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import {
	Alert,
	Box,
	Button,
	Grid,
	InputAdornment,
	OutlinedInput,
	Paper,
	Snackbar,
	TextField,
	Typography,
} from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Link } from 'react-router-dom';
import { contactUs } from '../../../services/api/apiManager';
import * as Yup from 'yup';
import MaskedInput from 'react-text-mask';
import './ContactUs.css';

function ContactUs() {
	const defaultTheme = createTheme();
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [errorSnackbar, setErrorSnackbar] = useState(false);
	const [error401Snackbar, setError401Snackbar] = useState(false);

	const schema = Yup.object().shape({
		name: Yup.string()
			.required('Required*')
			.matches(/^[a-zA-Z\s]+$/, 'Name should contain only alphabets'),
		Email: Yup.string().matches(
			/^[A-Za-z0-9]+[-A-Za-z0-9._]*@([-A-Za-z0-9]+[^@])+[A-Za-z]{2,5}$/,
			'Wrong Email'
		),
	});

	const [errors, setErrors] = useState({});

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));

		Yup.reach(schema, name)
			.validate(value)
			.then(() => {
				setErrors((prevErrors) => ({
					...prevErrors,
					[name]: '',
				}));
			})
			.catch((error) => {
				setErrors((prevErrors) => ({
					...prevErrors,
					[name]: error.message,
				}));
			});
	};

	const [formData, setFormData] = useState({
		Uid: 2147483647,
		name: '',
		ContactNo: '',
		Email: '',
		Subject: '',
		Description: '',
		Status: 'string',
	});

	const handleSubmit = async () => {
		try {
			if (Object.values(formData).some((value) => value === '')) {
				setErrorSnackbar(true);
				return;
			}
			await schema.validate(formData, { abortEarly: false });
			const response = await contactUs(formData);

			if (response?.status === 200) {
				setOpenSnackbar(true);
				setTimeout(() => {
					window.location.reload();
				}, 1500);
			} else if (response?.status === 401) {
				setError401Snackbar(true);
			} else {
				setError401Snackbar(true);
			}
		} catch (error) {
			// Keep existing behavior unchanged.
		}
	};

	const handleSnackbarClose = () => {
		setOpenSnackbar(false);
		setErrorSnackbar(false);
		setError401Snackbar(false);
	};

	const handleChange = (event) => {
		const { name, value } = event.target;
		setFormData({ ...formData, [name]: value });
	};

	return (
		<ThemeProvider theme={defaultTheme}>
			<Grid
				container
				component="main"
				className="contact-page"
				sx={{
					minHeight: '100vh',
					backgroundImage: `url(${require('../../../../assets/images/Authentication-bg.webp')})`,
					backgroundRepeat: 'no-repeat',
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					padding: { xs: '14px', sm: '20px', md: '28px 36px' },
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<Grid
					container
					item
					justifyContent="center"
					className="contact-shell"
					sx={{
						width: '100%',
						maxWidth: '1040px',
						borderRadius: { xs: '16px', md: '26px' },
						overflow: 'hidden',
						boxShadow: '0 28px 70px rgba(74, 95, 132, 0.22)',
						border: '1px solid rgba(255,255,255,0.72)',
						backgroundColor: 'rgba(255,255,255,0.68)',
						backdropFilter: 'blur(8px)',
					}}
				>
					<Grid
						item
						md={5}
						lg={5}
						xl={4.5}
						className="contact-brand-panel"
						sx={{ display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							minHeight: '560px',
							display: { sm: 'none', xs: 'none', md: 'flex' } }}
					>
						<Box className="contact-brand-copy">
							<Typography className="contact-brand-title">
								Get In Touch
							</Typography>
							<Box className="contact-brand-accent" />
							<Typography className="contact-brand-subtitle">
								Reach out for product questions, partnership conversations, or
								support. We&apos;ll help route your message quickly.
							</Typography>
						</Box>

						<Box className="contact-brand-footer">
							<Box className="contact-feature-card">
								<Box className="contact-feature-icon">
									<CallIcon />
								</Box>
								<Box>
								<Typography className="contact-feature-title">
									Fast response
								</Typography>
								<Typography className="contact-feature-text">
									Share your details and our team can review your request with
									the right context.
								</Typography>
								</Box>
							</Box>
							<Box className="contact-feature-card">
								<Box className="contact-feature-icon">
									<ContactSupportIcon />
								</Box>
								<Box>
								<Typography className="contact-feature-title">
									Clear follow-up
								</Typography>
								<Typography className="contact-feature-text">
									Use the form to send complete information so support stays
									efficient and organized.
								</Typography>
								</Box>
							</Box>
						</Box>

						<Box className="contact-links-row">
							<Box className="contact-link-item">
								<ContactSupportIcon className="contact-link-icon" />
								<Link to="/aboutUs" className="contact-side-link">
									<Typography>About Us</Typography>
								</Link>
							</Box>
							<Box className="contact-link-item">
								<CallIcon className="contact-link-icon" />
								<Link to="/contactUs" className="contact-side-link">
									<Typography>Contact Us</Typography>
								</Link>
							</Box>
						</Box>
					</Grid>

					<Grid
						component={Paper}
						square
						item
						xs={12}
						md={7}
						lg={7}
						xl={7.5}
						className="contact-form-panel"
						sx={{
							backgroundImage: `url(${require('../../../../assets/images/formbackground.png')})`,
							backgroundRepeat: 'no-repeat',
							backgroundSize: 'cover',
							backgroundPosition: 'center',
						}}
					>
						<Box className="contact-form-wrap">
							<Box className="contact-form-logo">
								<img
									src={websitelogo}
									alt="MaxCharts"
									style={{ width: '170px', height: 'auto', objectFit: 'contain' }}
								/>
							</Box>

							<Typography className="contact-form-title">Contact Us</Typography>
							<Typography className="contact-form-subtitle">
								Send us your details and a short message. We&apos;ll direct your
								request to the right team.
							</Typography>

							<Box className="contact-form-fields">
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<Typography className="contact-field-label">Name</Typography>
										<OutlinedInput
											fullWidth
											required
											name="name"
											value={formData.name}
											onChange={handleInputChange}
											className="contact-input"
											startAdornment={
												<InputAdornment position="start">
													<PersonOutlineOutlinedIcon className="contact-input-icon" />
												</InputAdornment>
											}
										/>
										{errors.name && (
											<div className="contact-error-text">{errors.name}</div>
										)}
									</Grid>
									<Grid item xs={12} sm={6}>
										<Typography className="contact-field-label">
											Contact #
										</Typography>
										<TextField
											variant="outlined"
											fullWidth
											required
											name="ContactNo"
											value={formData.ContactNo}
											onChange={handleChange}
											className="contact-text-field"
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<CallIcon className="contact-input-icon" />
													</InputAdornment>
												),
												inputComponent: MaskedInput,
												inputProps: {
													mask: [
														'(',
														/\d/,
														/\d/,
														/\d/,
														')',
														' ',
														/\d/,
														/\d/,
														/\d/,
														'-',
														/\d/,
														/\d/,
														/\d/,
														/\d/,
													],
												},
											}}
										/>
									</Grid>
								</Grid>

								<Box>
									<Typography className="contact-field-label">
										Email Address
									</Typography>
									<OutlinedInput
										fullWidth
										type="email"
										name="Email"
										value={formData.Email}
										id="email"
										size="small"
										autoComplete="email"
										required
										onChange={handleInputChange}
										className="contact-input"
										startAdornment={
											<InputAdornment position="start">
												<MailOutlineOutlinedIcon className="contact-input-icon" />
											</InputAdornment>
										}
									/>
									{errors.Email && (
										<div className="contact-error-text">{errors.Email}</div>
									)}
								</Box>

								<Box>
									<Typography className="contact-field-label">Subject</Typography>
									<OutlinedInput
										fullWidth
										type="text"
										name="Subject"
										value={formData.Subject}
										onChange={handleChange}
										required
										id="subject"
										className="contact-input"
										startAdornment={
											<InputAdornment position="start">
												<BorderColorOutlinedIcon className="contact-input-icon" />
											</InputAdornment>
										}
									/>
								</Box>

								<Box>
									<Typography className="contact-field-label">
										Description
									</Typography>
									<TextField
										multiline
										fullWidth
										name="Description"
										value={formData.Description}
										onChange={handleChange}
										rows={4}
										variant="outlined"
										className="contact-text-area"
									/>
								</Box>
							</Box>

							<Button
								variant="contained"
								onClick={() => handleSubmit()}
								size="small"
								fullWidth
								className="contact-submit-button"
							>
								Submit
							</Button>

							<Box className="contact-divider-row">
								<Box className="contact-divider-line" />
								<Box className="contact-divider-badge">or</Box>
								<Box className="contact-divider-line" />
							</Box>

							<Box className="contact-login-row">
								<Typography className="contact-login-text">
									Want to go back?
								</Typography>
								<Link to="/" className="contact-login-link">
									<Typography>Login</Typography>
								</Link>
							</Box>
						</Box>
					</Grid>

					<Snackbar
						open={openSnackbar}
						autoHideDuration={3000}
						onClose={handleSnackbarClose}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
					>
						<Alert onClose={handleSnackbarClose} severity="success">
							Recorded successfully.
						</Alert>
					</Snackbar>

					<Snackbar
						open={error401Snackbar}
						autoHideDuration={6000}
						onClose={handleSnackbarClose}
						anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
					>
						<Alert
							onClose={handleSnackbarClose}
							severity="error"
							sx={{
								width: '100%',
								background: 'red',
								color: 'white',
								fontFamily: 'Poppins',
								'& .MuiAlert-icon': {
									color: 'white',
								},
							}}
						>
							Error 401
						</Alert>
					</Snackbar>

					<Snackbar
						open={errorSnackbar}
						autoHideDuration={6000}
						onClose={handleSnackbarClose}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
					>
						<Alert
							onClose={handleSnackbarClose}
							severity="error"
							sx={{
								width: '100%',
								background: 'red',
								color: 'white',
								fontFamily: 'Poppins',
								'& .MuiAlert-icon': {
									color: 'white',
								},
							}}
						>
							Please fill in all fields.
						</Alert>
					</Snackbar>
				</Grid>
			</Grid>
		</ThemeProvider>
	);
}

export default ContactUs;
